import type { ComponentType, ReactNode } from 'react';
// @ts-ignore
import EnllacComponent from 'components/corporatiu/Enllac';
const Enllac: any = EnllacComponent;
import { useRouter } from 'next/router';
import styles from './multiidioma.module.scss';
import type { MultiIdiomaProps } from './MultiIdioma.types';

const EnllacLink = Enllac as ComponentType<{
    href: string;
    title?: string;
    className?: string;
    hrefLang?: string;
    rel?: string;
    children?: ReactNode;
}>;

const MultiIdioma = (props: MultiIdiomaProps) => {
    const router = useRouter();
    const currentPath = router.asPath;

    const cfg = (props as any)?.props ?? props;

    const langMap = {
        cat: { code: 'ca', label: 'CAT', title: 'CAT' },
        cast: { code: 'es', label: 'CAST', title: 'CAST' },
        eng: { code: 'en', label: 'ENG', title: 'ENG' },
        fr: { code: 'fr', label: 'FR', title: 'FR' }
    } as const;

    type LangKey = keyof typeof langMap;
    type Lang = (typeof langMap)[LangKey];

    const isLangKey = (value: string): value is LangKey => value in langMap;

    const allowedLangs: LangKey[] = (cfg?.opcions || 'cat|cast|eng|fr')
        .split('|')
        .map((value: string) => value.trim())
        .filter(isLangKey);

    const languages: Lang[] = allowedLangs.map((key) => langMap[key]);

    const getCurrentLangFromUrls = () => {
        if (!cfg?.urls) return null;

        const pathOnly = currentPath.split(/[?#]/)[0];
        const entry = Object.entries(cfg.urls as Record<string, string>).find(
            ([, url]) => pathOnly === url || pathOnly.replace(/\/$/, '') === url.replace(/\/$/, '')
        );

        return entry ? entry[0] : null;
    };

    const getCurrentLang = () => {
        const urlsLang = getCurrentLangFromUrls();
        if (urlsLang) return urlsLang;

        const queryLang = router?.query?.lang;
        if (queryLang) return String(queryLang);

        const match = currentPath.match(/\/(ca|es|en|fr)(?:\/|$)/);
        return match ? match[1] : 'ca';
    };

    const currentLang = getCurrentLang();

    const buildUrlFromPattern = (targetLang: string) => {
        const pattern = cfg?.urlPattern;
        if (!pattern) return null;

        let resolved = pattern;

        // Si hi ha {slug}, agafa tot el path després del segment /ca|/es|/en|/fr
        if (resolved.includes('{slug}')) {
            const pathOnly = currentPath.split(/[?#]/)[0];
            const langMatch = pathOnly.match(/\/(?:ca|es|en|fr)(?:\/|$)/);

            let rawSlug = '';
            if (langMatch?.index !== undefined) {
                rawSlug = pathOnly.slice(langMatch.index + langMatch[0].length);
            }

            // Si el patró és ".../{slug}", evitem doble slash
            const slug = resolved.includes('/{slug}') ? rawSlug.replace(/^\/+/, '') : rawSlug;

            resolved = resolved.replace('{slug}', slug);
        }

        if (cfg?.omitLangForCa && targetLang === 'ca') {
            return resolved.replace(/\/?\{lang\}\/?/, '/').replaceAll('//', '/');
        }

        return resolved.replace('{lang}', targetLang);
    };

    const getUrlForLang = (targetLang: string) => {
        if (cfg?.urls && cfg.urls[targetLang]) {
            return cfg.urls[targetLang];
        }

        return buildUrlFromPattern(targetLang) || currentPath;
    };

    return (
        <nav className={styles.nav} aria-label='MultiIdioma'>
            <ul>
                {languages.map((lang, idx) => (
                    <li key={lang.code} className={idx === languages.length - 1 ? 'last-child' : undefined}>
                        <EnllacLink
                            href={getUrlForLang(lang.code)}
                            title={lang.title}
                            className={currentLang === lang.code ? styles.active : ''}
                            hrefLang={currentLang !== lang.code ? lang.code : undefined}
                            rel={currentLang !== lang.code ? 'alternate' : undefined}
                        >
                            {lang.label}
                        </EnllacLink>
                    </li>
                ))}
            </ul>
        </nav>
    );
};

export default MultiIdioma;
